import type {
  ErrorObject,
  JSONSchemaType,
  ValidateFunction,
  Ajv2020
} from 'ajv/dist/2020';
import { useTranslation } from '@/plugins/i18n/useTranslation';
import { CustomErrorKey } from './constants/customErrorKey';
import { addCustomValidators } from './schemas/validators/customValidators';

let ajvInstance: Ajv2020 | null = null;

export async function initializeAjv(): Promise<Ajv2020> {
  if (ajvInstance) {
    return ajvInstance;
  }

  const { Ajv2020 } = await import('ajv/dist/2020');
  const ajvErrors = (await import('ajv-errors')).default;
  const ajvFormats = (await import('ajv-formats')).default;

  ajvInstance = new Ajv2020({ allErrors: true });

  ajvFormats(ajvInstance);
  ajvErrors(ajvInstance);
  addCustomValidators(ajvInstance);

  return ajvInstance;
}

export async function removeJsonSchemaFromCache(schemaId: string): Promise<void> {
  const ajv = await initializeAjv();
  ajv.removeSchema(schemaId);
}

export function useJsonValidation<T>(
  schema: JSONSchemaType<T>
) {
  const { t } = useTranslation();

  function addDefinitions(ajv: Ajv2020, definitions: JSONSchemaType<Partial<T>>[] = []) {
    if (definitions?.length) {
      definitions.forEach((definition) => {
        if (!definition.$id) {
          return;
        }
        if (!ajv.getSchema(definition.$id)) {
          ajv.addSchema(definition);
        }
      });
    }
  }

  function getValidation(ajv: Ajv2020): ValidateFunction<JSONSchemaType<T>> {
    return (schema.$id && ajv.getSchema(schema.$id)) || ajv.compile<JSONSchemaType<T>>(schema);
  }

  function parseErrorMessage<T extends object>(
    data: T,
    instancePath: string,
    errorMessage: string,
    error: ErrorObject
  ): string {
    const argumentRegex = /##(.*?)##/;
    const customParamRegex = /{(.*?)}/;
    const errorParams = errorMessage.match(argumentRegex);
    if (!errorParams) {
      return errorMessage;
    }
    const cleanedString = errorMessage.replace(argumentRegex, '');
    const params = errorParams[1].split(';').map((param: string): string => param.trim());
    const translationArgs = params.map((param: string): string => {
      if (param.match(customParamRegex)) {
        if (param.includes(CustomErrorKey.InstancePathParamPosition)) {
          const instanceParamCleaned = param.replace(customParamRegex, '$1');
          const instanceParamPath = instanceParamCleaned.split(':')[1].trim();
          if (instanceParamPath.includes('-')) {
            const indexes = instanceParamPath.split('-').map(Number);
            return instancePath.split('/').filter(Boolean).slice(indexes[0], indexes[1]).join('.');
          }
          return instancePath.split('/').filter(Boolean)[+instanceParamPath];
        }
        if (param.includes(CustomErrorKey.ErrorParam)) {
          const dataParamCleaned = param.replace(customParamRegex, '$1');
          const errorParam = dataParamCleaned.split(':')[1].trim();
          const selectedErrors = error.params.errors.filter(
            (err: ErrorObject): boolean => err.params[errorParam]
          );
          return selectedErrors.map((e: ErrorObject): string => e.params[errorParam] || '').filter(Boolean).join(', ');
        }
        if (param.includes(CustomErrorKey.StaticErrorParam)) {
          const staticParamCleaned = param.replace(customParamRegex, '$1');
          const staticParam = staticParamCleaned.split(':')[1].trim();
          return t(staticParam);
        }
      }
      const argumentPaths = param.split('/').filter(Boolean);
      const turnBacks = argumentPaths.filter((nesting: string): boolean => nesting === '..').length;
      const instancePathParams = !turnBacks
        ? instancePath.split('/').filter(Boolean)
        : instancePath.split('/').filter(Boolean).slice(0, -turnBacks);
      let value: any = data;
      instancePathParams.forEach((split: string) => {
        value = value[split as keyof T];
      });
      argumentPaths
        .filter((nesting) => nesting !== '..' && nesting !== '.')
        .forEach((nesting) => {
          value = value[nesting as keyof T];
        });
      return value;
    });
    return t(cleanedString, translationArgs);
  }

  return {
    getValidation,
    addDefinitions,
    parseErrorMessage
  };
}
